import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { PriceTrendData } from '../../types/unit-history';
import { format } from 'date-fns';
import { CurrencyEuroIcon } from '@heroicons/react/24/outline';

interface PriceTrendChartProps {
  data: PriceTrendData[];
}

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
};

const formatTooltipValue = (value: number, name: string) => {
  if (name === 'price') {
    return [formatPrice(value), 'Price'];
  }
  if (name === 'change') {
    return [`${value}%`, 'Change'];
  }
  return [value, name];
};

export const PriceTrendChart: React.FC<PriceTrendChartProps> = ({ data }) => {
  if (data.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 mx-auto mb-4 bg-secondary-100 rounded-full flex items-center justify-center">
          <CurrencyEuroIcon className="h-8 w-8 text-secondary-400" />
        </div>
        <h3 className="text-lg font-medium text-secondary-900 mb-2">No price data available</h3>
        <p className="text-secondary-500">Price trends will appear here once data is available.</p>
      </div>
    );
  }

  const chartData = data.map(item => {
    // Ensure date is properly parsed
    const date = item.date instanceof Date ? item.date : new Date(item.date);
    
    // Validate date
    if (isNaN(date.getTime())) {
      console.warn('Invalid date found in price trend data:', item.date);
      return null;
    }
    
    return {
      ...item,
      date,
      formattedDate: format(date, 'dd/MM'),
      formattedPrice: formatPrice(item.price)
    };
  }).filter(Boolean);

  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis 
            dataKey="formattedDate" 
            stroke="#64748b"
            fontSize={12}
          />
          <YAxis 
            stroke="#64748b"
            fontSize={12}
            tickFormatter={(value) => formatPrice(value)}
          />
          <Tooltip 
            formatter={formatTooltipValue}
            labelFormatter={(label) => `Date: ${label}`}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
          />
          <Line 
            type="monotone" 
            dataKey="price" 
            stroke="#0284c7" 
            strokeWidth={2}
            dot={{ fill: '#0284c7', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#0284c7', strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
